import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import {
  CenterAlignedCell,
  CenterAlignedHeader,
  RightAlignedCell,
  RightAlignedHeader,
} from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { ISiteSettingsForServerAdmin } from '../../settings/interfaces/ISite';
import { ISiteAdminSettings } from '../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { SizeFilter } from '../../ts/filters/SizeFilter';
import { L10n } from '../../ts/L10n/L10n';
import { ExtractEncryptionMode } from '../../ts/resources/Sites';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ServerService } from '../../ts/services/ServerService';

export module SiteGrid {

  export function columns(
    translate: IReactTranslateService,
    scope: DataGridCells.ISelectedItemsScope<ISiteSettingsForServerAdmin>,
    actions: IActionButton<ISiteSettingsForServerAdmin, {}>[],
    getActionTarget: () => IActionTarget<ISiteSettingsForServerAdmin, {}>,
    siteUrls: SiteUrls,
    server: ServerService)
  : DataGrid.Column[] {

    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope),
      nameColumn(translate, siteUrls, actions, getActionTarget, scope),
      {
        name: 'user-count-col',
        dataKey: 'userCount',
        headerText: translate('column_userCount'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('userCount')),
        direction: 'asc',
      },
      {
        name: 'admin-count-col',
        dataKey: 'adminCount',
        headerText: translate('column_adminCount'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('adminCount')),
        direction: 'asc',
      },
      {
        name: 'user-quota-col',
        dataKey: 'userQuota',
        headerText: translate('column_maxUsers'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(userQuotaFormatter()),
        direction: 'asc',
      },
      {
        name: 'storage-used-col',
        dataKey: 'storageUsedBytes',
        headerText: translate('column_storageUsed'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(StorageUsedFormatter),
        direction: 'asc',
      },
      {
        name: 'storage-quota-col',
        dataKey: 'storageQuotaBytes',
        headerText: translate('column_storageQuota'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(StorageQuotaFormatter),
        direction: 'asc',
      },
      {
        name: 'status-col',
        dataKey: 'availability',
        headerText: translate('column_status'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('availability', translate),
        direction: 'asc'
      },
      {
        name: 'metrics-col',
        dataKey: 'metricsEnabled',
        headerText: translate('column_metrics'),
        headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: CenterAlignedCell(DataGridCells.Checkmark('settings.metricsEnabled')),
        direction: 'desc',
      },
      {
        name: 'web-authoring-col',
        dataKey: 'authoringEnabled',
        headerText: translate('column_webAuthoring'),
        headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: CenterAlignedCell(DataGridCells.Checkmark('settings.authoringEnabled')),
        direction: 'desc',
      },
      {
        name: 'sheet-image-col',
        dataKey: 'sheetImageEnabled',
        headerText: translate('column_sheetImage'),
        headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: CenterAlignedCell(DataGridCells.Checkmark('siteAdminSettings.sheetImageEnabled')),
        direction: 'desc',
      }
    ];

    columns.push({
      name: 'edit-membership-col',
      dataKey: 'siteAdminsCanAddUsers',
      headerText: translate('column_editMembership'),
      headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: CenterAlignedCell(DataGridCells.Checkmark('settings.siteAdminsCanAddUsers')),
      direction: 'desc',
    });

    if (server.isExtractEncryptionEnabledOmniSwitch()) {
        const siteStatusToMessageKey: _.Dictionary<string> = {};
        siteStatusToMessageKey[ExtractEncryptionMode.ENFORCED] = 'enforced';
        siteStatusToMessageKey[ExtractEncryptionMode.ENABLED] = 'enabled';
        siteStatusToMessageKey[ExtractEncryptionMode.DISABLED] = 'disabled';

        columns.push({
          name: 'extract-encryption-mode-col',
          dataKey: 'extractEncryptionMode',
          headerText: translate('column_extractEncryptionMode'),
          headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
          cellFormatter: CenterAlignedCell(DataGridCells.PlainTextCellWithTransform('siteAdminSettings',
            (siteAdminSettings: ISiteAdminSettings) => translate(siteStatusToMessageKey[siteAdminSettings.extractEncryptionMode]))),
          direction: 'desc',
        });
    }

    if(FeatureFlags.guestAccountPerSiteEnabled && server.isGuestEnabled() === true) {
      columns.push({
        name: 'guestAccess-col',
        dataKey: 'guestAccessEnabled',
        headerText: translate('column_guestAccessEnabled'),
        headerFormatter: CenterAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: CenterAlignedCell(DataGridCells.Checkmark('settings.guestAccessEnabled')),
        direction: 'desc',
      });
    }

    columns.push({
      name: 'created-at-col',
      dataKey: 'createdAt',
      headerText: translate('column_created'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'desc',
      cellFormatter: DataGridCells.DateTimeCell('createdAt')
    });
    return columns;
  }

  function nameColumn(translate: IReactTranslateService,
                      siteUrls: SiteUrls,
                      actions: IActionButton<ISiteSettingsForServerAdmin, {}>[],
                      getActionTarget: () => IActionTarget<ISiteSettingsForServerAdmin, {}>,
                      scope: DataGridCells.ISelectedItemsScope<ISiteSettingsForServerAdmin>) {

    const iconTitle = translate('site');

    return CommonDataGridColumns.withActions({
        name: 'name-col',
        dataKey: 'name',
        headerText: translate('column_name'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
          const site = <ISiteSettingsForServerAdmin>row.data;
          return {
            text: site.settings.name,
            url: siteUrls.forSiteName(site.settings.urlName).projectsUrl(),
            iconClassName: 'tb-grid-icon-site',
            iconTitle: iconTitle
          };
        })
      }, {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope
    );
  }

  function userQuotaFormatter(): React.StatelessComponent<BodyCell.Props> {
    const serverLimitElement = React.createElement('span', {}, Messages.serverLimit());
    return (props: BodyCell.Props) => {
      const site = <ISiteSettingsForServerAdmin>props.row.data;
      if (site.settings.userQuotaEnabled) {
        return React.createElement('span', {}, L10n.localizedCount(site.settings.userQuota));
      } else {
        return serverLimitElement;
      }
    };
  }

  function StorageUsedFormatter(props: BodyCell.Props): JSX.Element {
    const site = <ISiteSettingsForServerAdmin>props.row.data;
    return React.createElement('span', {},
      site.settings.storageQuotaEnabled
        ? React.createElement('span', {}, '(' + L10n.localizedPercent(site.storageQuotaPercentUsed) + ') ')
        : null,
      React.createElement('span', {}, SizeFilter(site.storageUsedBytes))
    );
  }

  function StorageQuotaFormatter(props: BodyCell.Props): JSX.Element {
    const serverLimitElement = React.createElement('span', {}, Messages.serverLimit());
    const site = <ISiteSettingsForServerAdmin>props.row.data;
    if (site.settings.storageQuotaEnabled) {
      return React.createElement('span', {}, SizeFilter(site.settings.storageQuotaBytes));
    } else {
      return serverLimitElement;
    }
  }
}
